import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { UserPlus, Mail, Trash2, RefreshCw, Clock, CheckCircle, XCircle, Users, AlertTriangle } from 'lucide-react';
import { PendingUserInvitation } from '@/types/supabase';
import { BatchInviteDialog } from './batch-invite-dialog';

interface UserManagementProps {}

export const UserManagement: React.FC<UserManagementProps> = () => {
  const { toast } = useToast();
  const [invitations, setInvitations] = useState<PendingUserInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [batchInviteDialogOpen, setBatchInviteDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [singleInviteError, setSingleInviteError] = useState<string | null>(null);

  // Form state
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');

  useEffect(() => {
    fetchInvitations();
  }, []);

  const fetchInvitations = async () => {
    try {
      console.log('Fetching invitations from database...');
      
      const { data, error } = await supabase
        .from('pending_user_invitations')
        .select('*')
        .order('invited_at', { ascending: false });

      console.log('Database response:', { data, error, count: data?.length });

      if (error) {
        console.error('Database error:', error);
        throw error;
      }

      setInvitations(data || []);
      setDebugInfo({
        totalInvitations: data?.length || 0,
        lastFetch: new Date().toISOString(),
        databaseError: null
      });
      
    } catch (error: any) {
      console.error('Error fetching invitations:', error);
      setDebugInfo({
        totalInvitations: 0,
        lastFetch: new Date().toISOString(),
        databaseError: error.message
      });
      
      toast({
        variant: "destructive",
        title: "Feil ved henting av invitasjoner",
        description: `Database feil: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setSingleInviteError(null);

    try {
      console.log('Sending single invitation:', { email, role });
      
      const { data, error } = await supabase.functions.invoke('send-user-invitation', {
        body: { email, role }
      });

      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        setSingleInviteError(`Edge Function feil: ${error.message}`);
        throw error;
      }

      if (!data) {
        const errorMsg = 'Ingen respons fra Edge Function';
        console.error(errorMsg);
        setSingleInviteError(errorMsg);
        throw new Error(errorMsg);
      }

      if (data.success) {
        toast({
          title: "Invitasjon sendt!",
          description: `En invitasjons-e-post er sendt til ${email}.`
        });

        setEmail('');
        setRole('user');
        setInviteDialogOpen(false);
        fetchInvitations();
      } else {
        // Show detailed error from the response
        const errorDetails = data.errors && data.errors.length > 0 
          ? data.errors[0].error 
          : data.message || 'Ukjent feil oppstod';
        
        setSingleInviteError(errorDetails);
        
        toast({
          variant: "destructive",
          title: "Feil ved sending av invitasjon",
          description: errorDetails
        });
      }

    } catch (error: any) {
      console.error('Error sending invitation:', error);
      
      let errorMessage = 'Kunne ikke sende invitasjon.';
      if (error.message) {
        errorMessage = error.message;
      }
      
      if (!singleInviteError) {
        setSingleInviteError(errorMessage);
      }
      
      toast({
        variant: "destructive",
        title: "Feil ved sending av invitasjon",
        description: errorMessage
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelInvitation = async (id: string) => {
    try {
      console.log('Cancelling invitation:', id);
      
      const { error } = await supabase
        .from('pending_user_invitations')
        .update({ status: 'cancelled' })
        .eq('id', id);

      if (error) {
        console.error('Database error when cancelling:', error);
        throw error;
      }

      toast({
        title: "Invitasjon kansellert",
        description: "Invitasjonen er kansellert."
      });

      fetchInvitations();

    } catch (error: any) {
      console.error('Error cancelling invitation:', error);
      toast({
        variant: "destructive",
        title: "Feil ved kansellering",
        description: `Database feil: ${error.message}`
      });
    }
  };

  const getStatusBadge = (invitation: PendingUserInvitation) => {
    const isExpired = new Date(invitation.expires_at) < new Date();
    
    if (invitation.status === 'accepted') {
      return <Badge variant="default" className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Akseptert</Badge>;
    }
    if (invitation.status === 'cancelled') {
      return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Kansellert</Badge>;
    }
    if (isExpired) {
      return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Utløpt</Badge>;
    }
    return <Badge variant="outline"><Clock className="w-3 h-3 mr-1" />Venter</Badge>;
  };

  const activeInvitations = invitations.filter(inv => 
    inv.status === 'pending' && new Date(inv.expires_at) > new Date()
  );

  const expiredInvitations = invitations.filter(inv => 
    inv.status === 'pending' && new Date(inv.expires_at) <= new Date()
  );

  const completedInvitations = invitations.filter(inv => 
    inv.status === 'accepted' || inv.status === 'cancelled'
  );

  const handleDialogClose = () => {
    setEmail('');
    setRole('user');
    setSingleInviteError(null);
    setInviteDialogOpen(false);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open) {
      setInviteDialogOpen(true);
    } else {
      handleDialogClose();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">Brukeradministrasjon</h2>
          <p className="text-muted-foreground">
            Administrer brukerinvitasjoner og tilgang til systemet
          </p>
        </div>
        
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setBatchInviteDialogOpen(true)}
          >
            <Users className="mr-2 h-4 w-4" />
            Inviter flere
          </Button>
          
          <Dialog open={inviteDialogOpen} onOpenChange={handleDialogOpenChange}>
            <DialogTrigger asChild>
              <Button>
                <UserPlus className="mr-2 h-4 w-4" />
                Inviter bruker
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Inviter ny bruker</DialogTitle>
                <DialogDescription>
                  Send en invitasjons-e-post til en ny bruker
                </DialogDescription>
              </DialogHeader>

              {singleInviteError && (
                <Card className="border-red-200 bg-red-50">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-red-800">
                      <AlertTriangle className="h-4 w-4" />
                      <span className="font-medium">Detaljert feilmelding:</span>
                    </div>
                    <p className="text-red-700 mt-2 text-sm">{singleInviteError}</p>
                  </CardContent>
                </Card>
              )}

              <form onSubmit={handleSendInvitation} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-postadresse</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    placeholder="bruker@eksempel.no"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="role">Rolle</Label>
                  <Select value={role} onValueChange={(value: 'user' | 'admin') => setRole(value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Bruker</SelectItem>
                      <SelectItem value="admin">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex justify-end gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={handleDialogClose}
                  >
                    Avbryt
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? "Sender..." : "Send invitasjon"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <BatchInviteDialog
        open={batchInviteDialogOpen}
        onOpenChange={setBatchInviteDialogOpen}
        onSuccess={fetchInvitations}
      />

      {debugInfo?.databaseError && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertTriangle className="h-4 w-4" />
              <span className="font-medium">Database tilkoblingsfeil:</span>
            </div>
            <p className="text-red-700 mt-2 text-sm">{debugInfo.databaseError}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Aktive invitasjoner</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{activeInvitations.length}</div>
            {debugInfo && (
              <p className="text-xs text-muted-foreground mt-1">
                Sist oppdatert: {new Date(debugInfo.lastFetch).toLocaleTimeString('nb-NO')}
              </p>
            )}
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Utløpte invitasjoner</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{expiredInvitations.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Fullførte invitasjoner</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{completedInvitations.length}</div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="active" className="space-y-4">
        <TabsList>
          <TabsTrigger value="active">Aktive ({activeInvitations.length})</TabsTrigger>
          <TabsTrigger value="expired">Utløpte ({expiredInvitations.length})</TabsTrigger>
          <TabsTrigger value="completed">Fullførte ({completedInvitations.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="active">
          <Card>
            <CardHeader>
              <CardTitle>Aktive invitasjoner</CardTitle>
              <CardDescription>
                Invitasjoner som venter på svar og ikke er utløpt
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <p className="text-muted-foreground text-center py-8">
                  Laster invitasjoner...
                </p>
              ) : activeInvitations.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Ingen aktive invitasjoner
                </p>
              ) : (
                <div className="space-y-4">
                  {activeInvitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{invitation.email}</span>
                          <Badge variant="outline">{invitation.role}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Sendt {new Date(invitation.invited_at).toLocaleDateString('nb-NO')} • 
                          Utløper {new Date(invitation.expires_at).toLocaleDateString('nb-NO')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(invitation)}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelInvitation(invitation.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="expired">
          <Card>
            <CardHeader>
              <CardTitle>Utløpte invitasjoner</CardTitle>
              <CardDescription>
                Invitasjoner som har utløpt uten å bli akseptert
              </CardDescription>
            </CardHeader>
            <CardContent>
              {expiredInvitations.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Ingen utløpte invitasjoner
                </p>
              ) : (
                <div className="space-y-4">
                  {expiredInvitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{invitation.email}</span>
                          <Badge variant="outline">{invitation.role}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Sendt {new Date(invitation.invited_at).toLocaleDateString('nb-NO')} • 
                          Utløpt {new Date(invitation.expires_at).toLocaleDateString('nb-NO')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(invitation)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="completed">
          <Card>
            <CardHeader>
              <CardTitle>Fullførte invitasjoner</CardTitle>
              <CardDescription>
                Invitasjoner som er akseptert eller kansellert
              </CardDescription>
            </CardHeader>
            <CardContent>
              {completedInvitations.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Ingen fullførte invitasjoner
                </p>
              ) : (
                <div className="space-y-4">
                  {completedInvitations.map((invitation) => (
                    <div key={invitation.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <span className="font-medium">{invitation.email}</span>
                          <Badge variant="outline">{invitation.role}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Sendt {new Date(invitation.invited_at).toLocaleDateString('nb-NO')} • 
                          {invitation.status === 'accepted' ? 'Akseptert' : 'Kansellert'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        {getStatusBadge(invitation)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end">
        <Button variant="outline" onClick={fetchInvitations} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Oppdater
        </Button>
      </div>
    </div>
  );
};
